/**
 * Utility to detect if a message contains date/time related keywords
 * Used to intercept conversational date selection and force UI picker instead
 */

export const DATE_TIME_KEYWORDS = [
  // Days of week
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun',

  // Relative dates
  'tomorrow', 'today', 'tonight', 'next week', 'this week',
  'next', 'this', 'coming',

  // Time indicators
  'am', 'pm', 'a.m.', 'p.m.',
  'morning', 'afternoon', 'evening', 'noon',
  'o\'clock', 'oclock',

  // Time patterns (matches things like "2pm", "10:00", "3 pm")
  /\d{1,2}\s*(am|pm|a\.m\.|p\.m\.)/i,
  /\d{1,2}:\d{2}/,
  /\d{1,2}\s*o'?clock/i,

  // Months
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
  'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec',

  // Date patterns (matches things like "12/25", "Dec 25", "25th")
  /\d{1,2}\/\d{1,2}/,
  /\d{1,2}(st|nd|rd|th)/i,

  // Question about time/date
  'what time', 'when can', 'available when', 'available on',
  'available at', 'how about',
];

/**
 * Checks if a message contains date/time keywords or patterns
 * @param message - The user's message to check
 * @returns true if message appears to be specifying a date/time
 */
export function containsDateTimeReference(message: string): boolean {
  const lowerMessage = message.toLowerCase().trim();

  // Empty or very short messages don't count
  if (lowerMessage.length < 2) {
    return false;
  }

  // Check against all keywords and patterns
  return DATE_TIME_KEYWORDS.some(keyword => {
    if (typeof keyword === 'string') {
      // For string keywords, check if they're present as a word boundary
      const wordBoundaryRegex = new RegExp(`\\b${keyword}\\b`, 'i');
      return wordBoundaryRegex.test(lowerMessage);
    } else {
      // For regex patterns, test directly
      return keyword.test(message);
    }
  });
}

/**
 * Checks if message is a simple affirmative response to scheduling
 * These should NOT trigger the date/time picker
 */
export function isSimpleAffirmative(message: string): boolean {
  const lowerMessage = message.toLowerCase().trim();
  const affirmatives = [
    'yes', 'yeah', 'yep', 'sure', 'ok', 'okay', 'sounds good',
    'that works', 'perfect', 'great', 'good', 'fine'
  ];

  return affirmatives.some(word => lowerMessage === word || lowerMessage === `${word}.` || lowerMessage === `${word}!`);
}

/**
 * Detects if an AI message contains time slot options in a numbered list format
 * Examples:
 * - "1. Tomorrow at 11:00 AM"
 * - "2. Tomorrow at 2:00 PM"
 * - "3. Friday at 10:30 AM"
 *
 * @param message - The AI's message to check
 * @returns true if message contains numbered time slot options
 */
export function containsTimeSlotOptions(message: string): boolean {
  // Pattern 1: Numbered list with times (e.g., "1. Tomorrow at 11:00 AM")
  const numberedTimePattern = /\d+\.\s+\w+\s+at\s+\d{1,2}:\d{2}\s*(am|pm|AM|PM)/i;

  // Pattern 2: "Please select" or "choose" followed by time references
  const selectTimePattern = /(please select|choose|pick).*time.*slot/i;

  // Pattern 3: Multiple time mentions in one message
  const multipleTimesPattern = /\d{1,2}:\d{2}\s*(am|pm|AM|PM).*\d{1,2}:\d{2}\s*(am|pm|AM|PM)/i;

  // Pattern 4: "options below" or "convenient time from" suggesting a list
  const listIndicatorPattern = /(options below|time from the options|convenient time from)/i;

  return numberedTimePattern.test(message) ||
         (selectTimePattern.test(message) && containsDateTimeReference(message)) ||
         (multipleTimesPattern.test(message) && listIndicatorPattern.test(message));
}
